import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

const STATUSES = ['pending','confirmed','processing','shipped','delivered','cancelled'];
const STATUS_COLORS = { pending:'badge-gray', confirmed:'badge-navy', processing:'badge-navy', shipped:'badge-green', delivered:'badge-green', cancelled:'badge-red' };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [updating, setUpdating] = useState(false);
  const [updateForm, setUpdateForm] = useState({ status: '', tracking_number: '' });

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 50 };
    if (statusFilter) params.status = statusFilter;
    api.get('/orders/admin/all', { params }).then(r => { setOrders(r.data.orders || []); setTotal(r.data.total || 0); }).catch(() => {}).finally(() => setLoading(false));
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openOrder = async (order) => {
    const r = await api.get(`/orders/admin/${order.id}`);
    setSelected(r.data);
    setUpdateForm({ status: r.data.status, tracking_number: r.data.tracking_number || '' });
  };

  const updateStatus = async () => {
    setUpdating(true);
    try { await api.patch(`/orders/admin/${selected.id}/status`, updateForm); load(); setSelected(s => ({ ...s, ...updateForm })); } catch {}
    setUpdating(false);
  };

  if (selected) return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>← Orders</button>
        <h2 style={{ fontWeight: 800, color: '#1a2744', fontSize: 18 }}>Order {selected.order_number}</h2>
        <span className={`badge ${STATUS_COLORS[selected.status] || 'badge-gray'}`} style={{ textTransform: 'capitalize' }}>{selected.status}</span>
      </div>

      <div className="grid-2" style={{ gap: 20, alignItems: 'start' }}>
        <div>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <h3 style={{ fontWeight: 700, color: '#1a2744', marginBottom: 14, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Items</h3>
            {selected.items?.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f4f4f4', fontSize: 13 }}>
                <span style={{ fontWeight: 600, color: '#1a2744' }}>{item.title}{item.is_firearm ? ' 🔒' : ''}</span>
                <span style={{ fontWeight: 700 }}>${item.price?.toFixed(2)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 15, color: '#1a2744', paddingTop: 12, marginTop: 4 }}>
              <span>Order Total</span><span>${parseFloat(selected.total).toFixed(2)}</span>
            </div>
          </div>

          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <h3 style={{ fontWeight: 700, color: '#1a2744', marginBottom: 14, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer & Shipping</h3>
            <div style={{ fontSize: 13, color: '#444', lineHeight: 1.9 }}>
              <div><strong>Name:</strong> {selected.ship_name || selected.guest_name}</div>
              <div><strong>Email:</strong> {selected.guest_email}</div>
              {selected.shipping_method === 'pickup'
                ? <div><strong>Delivery:</strong> <span className="badge badge-green">🏪 In-store pickup</span></div>
                : <>
                    <div><strong>Address:</strong> {selected.ship_address1}{selected.ship_address2 ? `, ${selected.ship_address2}` : ''}, {selected.ship_city}, {selected.ship_state} {selected.ship_zip}</div>
                    <div><strong>Shipping charged:</strong> ${parseFloat(selected.shipping_cost || 0).toFixed(2)}</div>
                  </>}
              <div><strong>Payment:</strong> {selected.payment_method} · {selected.payment_status}</div>
              {selected.tracking_number && <div><strong>Tracking:</strong> {selected.tracking_number}</div>}
            </div>
          </div>
        </div>

        <div>
          {selected.ffl_dealer_name && (
            <div style={{ background: 'rgba(230,81,0,0.06)', border: '1px solid rgba(230,81,0,0.2)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <h3 style={{ fontWeight: 700, color: '#e65100', marginBottom: 12, fontSize: 14 }}>🔒 FFL Transfer Info</h3>
              <div style={{ fontSize: 13, color: '#555', lineHeight: 1.9 }}>
                <div><strong>Dealer:</strong> {selected.ffl_dealer_name}</div>
                {selected.ffl_dealer_address && <div><strong>Address:</strong> {selected.ffl_dealer_address}</div>}
                {selected.ffl_dealer_phone && <div><strong>Phone:</strong> {selected.ffl_dealer_phone}</div>}
                <div><strong>License #:</strong> {selected.ffl_license_number}</div>
              </div>
            </div>
          )}

          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <h3 style={{ fontWeight: 700, color: '#1a2744', marginBottom: 14, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Update Order</h3>
            <div className="form-group"><label className="form-label">Status</label>
              <select className="form-control" value={updateForm.status} onChange={e => setUpdateForm(f => ({ ...f, status: e.target.value }))}>
                {STATUSES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Tracking Number</label><input className="form-control" value={updateForm.tracking_number} onChange={e => setUpdateForm(f => ({ ...f, tracking_number: e.target.value }))} placeholder="Carrier tracking #" /></div>
            <button className="btn btn-primary" onClick={updateStatus} disabled={updating} style={{ width: '100%', justifyContent: 'center' }}>{updating ? 'Saving...' : 'Update Order'}</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2744', marginBottom: 2 }}>Orders</h1>
          <p style={{ color: '#9a9a9a', fontSize: 13 }}>{total} total orders</p>
        </div>
        <select className="form-control" style={{ width: 'auto' }} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        {loading ? <div className="loading"><div className="spinner" /></div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f7f2', borderBottom: '2px solid #e8e8e8' }}>
                {['Order #','Customer','Date','Total','Status','Payment',''].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#9a9a9a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#9a9a9a' }}>No orders found</td></tr>
              : orders.map(o => (
                <tr key={o.id} style={{ borderBottom: '1px solid #f4f4f4', cursor: 'pointer' }} onClick={() => openOrder(o)}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '12px 16px', fontWeight: 700, color: '#1a2744', fontFamily: 'monospace', fontSize: 13 }}>{o.order_number}{o.shipping_method === 'pickup' && <span title="In-store pickup" style={{ marginLeft: 6 }}>🏪</span>}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#555' }}>{o.customer_name || o.guest_name || o.guest_email || 'Guest'}</td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#9a9a9a' }}>{new Date(o.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: '12px 16px', fontWeight: 700, color: '#1a2744' }}>${parseFloat(o.total).toFixed(2)}</td>
                  <td style={{ padding: '12px 16px' }}><span className={`badge ${STATUS_COLORS[o.status] || 'badge-gray'}`} style={{ fontSize: 10, textTransform: 'capitalize' }}>{o.status}</span></td>
                  <td style={{ padding: '12px 16px' }}><span className={`badge ${o.payment_status === 'paid' ? 'badge-green' : 'badge-gray'}`} style={{ fontSize: 10, textTransform: 'capitalize' }}>{o.payment_status}</span></td>
                  <td style={{ padding: '12px 16px' }}><button className="btn btn-ghost btn-sm">View →</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
